import { cn } from '../../lib/utils';

type PillTone = 'pos' | 'warn' | 'info' | 'danger' | 'muted';

const pillClass: Record<PillTone, string> = {
  pos: 'status-pill status-pill-pos',
  warn: 'status-pill status-pill-warn',
  info: 'status-pill status-pill-info',
  danger: 'status-pill status-pill-danger',
  muted: 'status-pill status-pill-muted',
};

const statusTone: Record<string, PillTone> = {
  ACTIVE: 'pos',
  SUSPENDED: 'warn',
  BANNED: 'danger',
};

interface StatusBadgeProps {
  status: string | null | undefined;
  /** Tooltip (e.g. the status reason). */
  title?: string;
  className?: string;
}

/** Account / organisation status pill: ACTIVE pos, SUSPENDED warn, BANNED danger. */
export default function StatusBadge({ status, title, className }: StatusBadgeProps) {
  const value = status || 'UNKNOWN';
  const tone = statusTone[value] ?? 'muted';
  return (
    <span className={cn(pillClass[tone], 'inline-flex items-center', className)} title={title}>
      {value}
    </span>
  );
}

const severityTone: Record<string, PillTone> = {
  INFO: 'info',
  WARN: 'warn',
  CRITICAL: 'danger',
};

export function SeverityBadge({ severity, className }: { severity: string | null | undefined; className?: string }) {
  const value = severity || 'INFO';
  const tone = severityTone[value] ?? 'muted';
  return <span className={cn(pillClass[tone], 'inline-flex items-center', className)}>{value}</span>;
}

/** Generic pill with an explicit tone (role pills, type pills, etc.). */
export function Pill({
  tone = 'muted',
  children,
  className,
  title,
}: {
  tone?: PillTone;
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span className={cn(pillClass[tone], 'inline-flex items-center', className)} title={title}>
      {children}
    </span>
  );
}

const roleTone: Record<string, PillTone> = {
  ADMIN: 'danger',
  AGENCY: 'info',
  BRAND: 'warn',
  KOL: 'pos',
};

export function RolePill({ role, className }: { role: string | null | undefined; className?: string }) {
  if (!role) return <span className="text-xs text-glass-text-3">—</span>;
  return (
    <Pill tone={roleTone[role] ?? 'muted'} className={className}>
      {role}
    </Pill>
  );
}
